/**
 * Read-only client for the publish-flow `blocked-decisions` endpoint:
 *
 *   GET /publish-flow/{jazelId}/{correlationId}/blocked-decisions
 *       ?pageSize=&cursor=&search=
 *
 * The blocked-VIN ROW LIST (with true rule attribution) is persisted server-side
 * as a JSONL artifact; this service pages and searches it so the dialog can show
 * which VINs were blocked and why without loading the full set up front, and can
 * find a VIN/rule that is not in the first page. Mirrors the request/auth
 * conventions of `publishFlowWebsiteRefreshKeysService`.
 */

export type BlockedDecisionClassification =
  | 'newlyBlocked'
  | 'alreadyBlocked'
  | 'unknownBlocked'

export type BlockedDecisionAttribution = 'authoritative' | 'recovered' | 'unavailable'

export type BlockedDecisionUnavailableReason =
  | 'SRP_MISSING'
  | 'RE_EVAL_NOT_BLOCKED'
  | 'CAP_EXCEEDED'
  | 'SRP_READ_FAILED'

export interface BlockedDecisionRecord {
  vin: string
  classification: BlockedDecisionClassification
  attribution: BlockedDecisionAttribution
  reason?: string
  ruleId?: string
  ruleName?: string
  field?: string
  operator?: string
  expectedValue?: unknown
  actualValue?: unknown
  feedSource?: string
  detailUnavailableReason?: BlockedDecisionUnavailableReason
  srpMissing?: boolean
}

export interface BlockedDecisionsPage {
  records: BlockedDecisionRecord[]
  totalRecords: number
  nextCursor?: string
  search?: string
  /** 'unavailable' on a graceful empty 200 (no metrics / no blocked-decisions pointer). */
  availability?: 'unavailable'
  reason?: 'noMetrics' | 'noPointer'
}

export type BlockedDecisionsReadResult =
  | { status: 'ok'; data: BlockedDecisionsPage }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route' | 'request'; message: string }

const trimBase = (endpoint: string) => endpoint.replace(/\/+$/g, '')

const authHeaders = (sessionToken: string) => ({
  Accept: 'application/json',
  Authorization: `Bearer ${sessionToken}`,
})

function blockedDecisionsPath(jazelId: string, correlationId: string): string {
  return `/publish-flow/${encodeURIComponent(jazelId)}/${encodeURIComponent(
    correlationId,
  )}/blocked-decisions`
}

function buildQuery(input: { pageSize?: number; cursor?: string; search?: string }): URLSearchParams {
  const query = new URLSearchParams()
  if (typeof input.pageSize === 'number' && Number.isFinite(input.pageSize)) {
    query.set('pageSize', String(input.pageSize))
  }
  if (typeof input.cursor === 'string' && input.cursor.length > 0) {
    query.set('cursor', input.cursor)
  }
  if (typeof input.search === 'string') {
    const trimmed = input.search.trim()
    if (trimmed.length > 0) query.set('search', trimmed)
  }
  return query
}

const CLASSIFICATIONS: ReadonlySet<BlockedDecisionClassification> = new Set([
  'newlyBlocked',
  'alreadyBlocked',
  'unknownBlocked',
])
const ATTRIBUTIONS: ReadonlySet<BlockedDecisionAttribution> = new Set([
  'authoritative',
  'recovered',
  'unavailable',
])

function normalizeRecord(raw: unknown): BlockedDecisionRecord | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const vin = typeof record.vin === 'string' ? record.vin : ''
  if (vin.length === 0) return null
  const classification = CLASSIFICATIONS.has(record.classification as BlockedDecisionClassification)
    ? (record.classification as BlockedDecisionClassification)
    : 'unknownBlocked'
  const attribution = ATTRIBUTIONS.has(record.attribution as BlockedDecisionAttribution)
    ? (record.attribution as BlockedDecisionAttribution)
    : 'unavailable'
  const out: BlockedDecisionRecord = { vin, classification, attribution }
  if (typeof record.reason === 'string' && record.reason.length > 0) out.reason = record.reason
  if (typeof record.ruleId === 'string' && record.ruleId.length > 0) out.ruleId = record.ruleId
  if (typeof record.ruleName === 'string' && record.ruleName.length > 0) out.ruleName = record.ruleName
  if (typeof record.field === 'string' && record.field.length > 0) out.field = record.field
  if (typeof record.operator === 'string' && record.operator.length > 0) out.operator = record.operator
  if (record.expectedValue !== undefined) out.expectedValue = record.expectedValue
  if (record.actualValue !== undefined) out.actualValue = record.actualValue
  if (typeof record.feedSource === 'string' && record.feedSource.length > 0) out.feedSource = record.feedSource
  if (typeof record.detailUnavailableReason === 'string') {
    out.detailUnavailableReason = record.detailUnavailableReason as BlockedDecisionUnavailableReason
  }
  if (record.srpMissing === true) out.srpMissing = true
  return out
}

function parsePage(data: unknown): BlockedDecisionsPage {
  const obj = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>
  const records = Array.isArray(obj.records)
    ? obj.records.map(normalizeRecord).filter((r): r is BlockedDecisionRecord => r !== null)
    : []
  const totalRecords =
    typeof obj.totalRecords === 'number' && Number.isFinite(obj.totalRecords)
      ? obj.totalRecords
      : records.length
  const page: BlockedDecisionsPage = { records, totalRecords }
  if (typeof obj.nextCursor === 'string' && obj.nextCursor.length > 0) {
    page.nextCursor = obj.nextCursor
  }
  if (typeof obj.search === 'string' && obj.search.length > 0) {
    page.search = obj.search
  }
  if (obj.availability === 'unavailable') {
    page.availability = 'unavailable'
    if (obj.reason === 'noMetrics' || obj.reason === 'noPointer') {
      page.reason = obj.reason
    }
  }
  return page
}

/**
 * Fetch one page of blocked-decision rows. Never throws on transport/HTTP
 * issues: a non-2xx or network failure surfaces as `unavailable` so the dialog
 * can show a graceful state. The backend returns an empty 200 page (with
 * `availability: 'unavailable'`) for runs with no blocked-decision artifact.
 */
export async function tryGetBlockedDecisions(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
  pageSize?: number
  cursor?: string
  search?: string
}): Promise<BlockedDecisionsReadResult> {
  const base = trimBase(params.endpoint)
  const path = blockedDecisionsPath(params.jazelId, params.correlationId)
  const qs = buildQuery({
    pageSize: params.pageSize,
    cursor: params.cursor,
    search: params.search,
  }).toString()
  const url = `${base}${path}${qs.length > 0 ? `?${qs}` : ''}`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: authHeaders(params.sessionToken),
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network error fetching blocked decisions.',
    }
  }

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const message =
      (payload && typeof payload === 'object' && typeof (payload as { message?: unknown }).message === 'string'
        ? (payload as { message: string }).message
        : '') || `Blocked decisions unavailable (${response.status}).`
    if (response.status >= 500) return { status: 'unavailable', reason: 'http5xx', message }
    if (response.status === 404) return { status: 'unavailable', reason: 'route', message }
    return { status: 'unavailable', reason: 'request', message }
  }

  const data =
    payload && typeof payload === 'object' && 'data' in payload
      ? (payload as { data: unknown }).data
      : payload
  return { status: 'ok', data: parsePage(data) }
}

/**
 * Presentation helpers — single source of truth for the approved UI wording so
 * the dialog and any future surface stay consistent.
 */
export function blockedDecisionAttributionLabel(record: BlockedDecisionRecord): string {
  switch (record.attribution) {
    case 'authoritative':
      return 'Blocked by this publish run'
    case 'recovered':
      return 'Still blocked under current rules'
    case 'unavailable':
    default:
      return 'Rule detail unavailable'
  }
}

export function blockedDecisionClassificationLabel(
  classification: BlockedDecisionClassification,
): string {
  switch (classification) {
    case 'newlyBlocked':
      return 'Newly blocked this run'
    case 'alreadyBlocked':
      return 'Already blocked before run'
    case 'unknownBlocked':
    default:
      return 'Blocked (unclassified)'
  }
}

/**
 * Human-readable "why" for a blocked row. SRP-missing gets a SPECIFIC message
 * (never a vague "unknown"); recoverable rule detail shows the rule;
 * truly-unavailable rows explain why detail could not be recovered.
 */
export function blockedDecisionReasonText(record: BlockedDecisionRecord): string {
  if (record.srpMissing || record.reason === 'SRP_MISSING') {
    return 'SRP object missing; vehicle cannot be safely published.'
  }
  if (record.ruleName || record.ruleId || record.field) {
    const ruleLabel = record.ruleName || record.ruleId || record.field
    if (record.field && record.operator) {
      return `Matched rule "${ruleLabel}" (${record.field} ${record.operator}).`
    }
    return `Matched rule "${ruleLabel}".`
  }
  if (record.reason) {
    return `Matched rule (${record.reason}).`
  }
  if (record.attribution === 'unavailable') {
    switch (record.detailUnavailableReason) {
      case 'RE_EVAL_NOT_BLOCKED':
        return 'No longer blocked under current rules; original rule detail is unavailable.'
      case 'CAP_EXCEEDED':
        return 'Rule detail unavailable (too many blocked vehicles to re-evaluate).'
      case 'SRP_READ_FAILED':
        return 'Rule detail unavailable (could not read vehicle data).'
      default:
        return 'Rule detail unavailable.'
    }
  }
  return 'Rule detail unavailable.'
}
